import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import type { LibraryView, Playlist } from "../api/types";
import { useServices } from "../app/services";
import { useAction } from "../app/useAction";
import { useResource } from "../app/useResource";
import { PerformanceCard, PostCard } from "../components/cards";
import { Icon } from "../components/Icon";
import { EmptyState, Field, PageHeader, Pill, ResourceView, TabPanel, Tabs } from "../components/ui";
import { performerNames } from "../lib/labels";
import { formatAgo, formatMs } from "../lib/time";

type Tab = "favorites" | "saved" | "moments" | "playlists" | "posts";
const TABS: Tab[] = ["favorites", "saved", "moments", "playlists", "posts"];
const TAB_LABEL: Record<Tab, string> = {
  favorites: "お気に入り",
  saved: "保存した投稿",
  moments: "ここ好き",
  playlists: "プレイリスト",
  posts: "自分の投稿",
};

/**
 * マイライブラリ：本人だけのもの。お気に入り（歌唱）と保存した投稿は別に扱う。
 * 削除された投稿は「削除済み」と出し、歌唱への参照は残す
 */
export function LibraryPage() {
  const { api } = useServices();
  const [params, setParams] = useSearchParams();
  const tab = (TABS.includes(params.get("tab") as Tab) ? params.get("tab") : "favorites") as Tab;
  const res = useResource("library", () => api.getLibrary(), [], ["library", "posts"]);
  const setTab = (t: Tab) => setParams(t === "favorites" ? {} : { tab: t }, { replace: true });
  const counts = res.status === "ready" ? count(res.data) : undefined;

  return (
    <div className="page">
      <PageHeader title="マイライブラリ" lead="あなただけのもの。ほかの人には見えません（公開した投稿・ここ好きを除く）。" />
      <Tabs
        idBase="lib"
        label="ライブラリの中身"
        value={tab}
        onChange={setTab}
        tabs={[
          { id: "favorites", label: TAB_LABEL.favorites, count: counts?.favorites },
          { id: "saved", label: TAB_LABEL.saved, count: counts?.saved },
          { id: "moments", label: TAB_LABEL.moments, count: counts?.moments },
          { id: "playlists", label: TAB_LABEL.playlists },
          { id: "posts", label: TAB_LABEL.posts, count: counts?.posts },
        ]}
      />
      {TABS.map((t) => (
        <TabPanel key={t} idBase="lib" id={t} active={tab === t}>
          <h2 className="visually-hidden">{TAB_LABEL[t]}</h2>
          {t === "playlists" ? (
            <PlaylistsTab />
          ) : (
            <ResourceView resource={res} what="ライブラリ">
              {(d) => <LibraryTab tab={t} data={d} />}
            </ResourceView>
          )}
        </TabPanel>
      ))}
    </div>
  );
}

function count(d: LibraryView) {
  return { favorites: d.saved_performances.length, saved: d.saved_posts.length, moments: d.moments.length, posts: d.posts.length };
}

function LibraryTab({ tab, data }: { tab: Tab; data: LibraryView }) {
  if (tab === "favorites") {
    return data.saved_performances.length ? (
      <ul className="perf-list">
        {data.saved_performances.map((s) => (
          <PerformanceCard key={s.id} performance={s.performance} />
        ))}
      </ul>
    ) : (
      <EmptyState title="お気に入りの歌唱はまだありません" icon="heart" ribatan={{ pose: "hello", say: "好きな歌、見つけよう！" }}>
        曲のページや「いま聴く」の♡から追加できます。
      </EmptyState>
    );
  }
  if (tab === "saved") {
    return data.saved_posts.length ? (
      <ul className="post-list">
        {data.saved_posts.map((s) =>
          s.post ? <PostCard key={s.id} post={{ ...s.post, performance: s.post.performance ?? s.performance }} /> : null,
        )}
      </ul>
    ) : (
      <EmptyState title="保存した投稿はまだありません" icon="bookmark">
        おすすめの投稿の「保存」から追加できます。
      </EmptyState>
    );
  }
  if (tab === "moments") {
    return data.moments.length ? (
      <ul className="my-moments">
        {data.moments.map((m) => (
          <li key={m.id} className="my-moment">
            <div className="my-moment__main">
              {m.performance ? (
                <p className="my-moment__title">
                  {m.performance.work.title}
                  <span>{performerNames(m.performance)}</span>
                </p>
              ) : (
                <p className="my-moment__title my-moment__title--unknown">
                  曲の情報を取得できません
                  <span>APIのここ好きに曲の情報が無いため（区間の版 {m.segment_revision_id.slice(0, 8)}…）</span>
                </p>
              )}
              <p className="my-moment__time">
                <Icon name="sparkle" className="inline-icon" />
                {formatMs(m.position_ms)}
                {m.end_ms !== null && `〜${formatMs(m.end_ms)}`}
                {m.created_at && <span className="muted"> ・ {formatAgo(m.created_at)}に記録</span>}
              </p>
              {m.body && <p className="my-moment__body">「{m.body}」</p>}
            </div>
            <div className="my-moment__side">
              {m.visibility === "members" ? <Pill tone="calm">公開中</Pill> : <Pill tone="draft">自分だけ</Pill>}
              {m.visibility === "members" && m.post_id && <Link className="btn btn-secondary btn-small" to={`/posts/${m.post_id}`}>投稿を見る・共有</Link>}
              <Link className="btn btn-secondary btn-small" to={`/moments/${m.id}`}>
                整える
                <span className="visually-hidden">
                  ：{m.performance?.work.title ?? "曲の情報なし"} の {formatMs(m.position_ms)}
                </span>
              </Link>
            </div>
          </li>
        ))}
      </ul>
    ) : (
      <EmptyState title="ここ好きはまだありません" icon="sparkle">
        VCで聴きながら「いま聴く」の「ここ好き」を押すと、その瞬間が自分だけの下書きとして残ります。
      </EmptyState>
    );
  }
  // 自分の投稿
  return data.posts.length ? (
    <ul className="post-list">
      {data.posts.map((p) => (
        <PostCard key={p.id} post={p} showVisibility />
      ))}
    </ul>
  ) : (
    <EmptyState title="まだ投稿していません" icon="edit">
      曲のページの「おすすめを書く」から投稿できます。
    </EmptyState>
  );
}

/* ---------------------------------------------------------------- プレイリスト（非公開） */

function PlaylistsTab() {
  const { api } = useServices();
  const res = useResource("playlists.list", () => api.getPlaylists(), []);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const create = useAction("playlists.create", (n: string) => api.createPlaylist(n));
  return (
    <>
      <form
        className="inline-form"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!name.trim()) return setError("名前を入れてください。");
          setError(null);
          const r = await create.run(name.trim());
          if (r.ok) {
            setName("");
            res.reload();
          }
        }}
      >
        <Field label="新しいプレイリスト" hint="自分だけに見えます。" error={error ?? create.error}>
          {({ id, describedBy, invalid }) => (
            <div className="input-row">
              <input
                id={id}
                className="input"
                value={name}
                maxLength={128}
                onChange={(e) => setName(e.target.value)}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                placeholder="夜に聴く"
                disabled={!create.connected}
              />
              <button type="submit" className="btn btn-secondary btn-small" disabled={create.running || !create.connected}>
                作る
              </button>
            </div>
          )}
        </Field>
      </form>
      <ResourceView resource={res} what="プレイリスト">
        {(lists: Playlist[]) =>
          lists.length ? (
            <ul className="playlist-list">
              {lists.map((pl) => (
                <li key={pl.id}>
                  <Link to={`/library/playlists/${pl.id}`} className="playlist-row">
                    <Icon name="queue" className="playlist-row__icon" />
                    <span className="playlist-row__name">{pl.name}</span>
                    <span className="playlist-row__count">{pl.performance_ids.length}曲</span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title="プレイリストはまだありません" icon="queue" />
          )
        }
      </ResourceView>
    </>
  );
}
